import React from "react";
import {
  useGetContactQuery,
  useGetTagsQuery,
  useGetDealsQuery,
  useGetTasksQuery,
  useBulkAddTagMutation,
  useBulkRemoveTagMutation,
  useUpdateContactMutation,
  useRestoreContactMutation,
  useDeleteContactPermanentlyMutation,
  useGetContactDuplicatesQuery,
} from "@/store/rest_api";
import { useNavigate } from "react-router-dom";
import { useCallDialog } from "@/context/CallDialogContext";
import { useMessageDialog } from "@/context/MessageDialogContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Phone, MessageSquare, ExternalLink, PhoneOff, MessageSquareOff, Plus, User, X, Handshake, CheckSquare, Calendar, ChevronDown, ArchiveRestore, Trash2, Merge, Pencil, MoreVertical, Globe, SlidersHorizontal } from "lucide-react";
import CustomFieldsEditor from "@/components/CustomFieldsEditor";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { getContactDisplayName, getInitials } from "@/util/string";
import { apiErrorMessage } from "@/util/apiError";
import VConExportButton from "@/components/vcon/VConExportButton";
import ActivityTimeline from "@/components/pages/contacts/ActivityTimeline";
import MergeContactDialog from "@/components/pages/contacts/MergeContactDialog";
import ContactConsentStatus from "@/components/contacts/ContactConsentStatus";
import TaskFormDialog from "@/components/pages/tasks/TaskFormDialog";
import { DealFormDialog } from "@/components/pages/deals/DealFormDialog";
import SkipTraceButton from "@/components/skip-trace/SkipTraceButton";
import ContactDncButton from "@/components/compliance/ContactDncButton";
import { DncListStatus } from "@/enum/DncListStatus";
import { ContactPhoneStatus } from "@/enum/ContactPhoneStatus";
import { SkipTraceScope } from "@/enum/SkipTraceScope";
import type { TaskStatus } from "@/model/interface/Task";

interface ContactActivityProps {
  contactId: string;
  compact?: boolean;
  showViewButton?: boolean;
  showManageActions?: boolean;
  headerExtra?: React.ReactNode;
}

const STATUS_LABELS: Record<TaskStatus, string> = {
  TODO: "To Do",
  IN_PROGRESS: "In Progress",
  DONE: "Done",
};

const ContactActivity: React.FC<ContactActivityProps> = ({
  contactId,
  compact,
  showViewButton = true,
  showManageActions = false,
  headerExtra,
}) => {
  const { data: contact, isLoading } = useGetContactQuery(contactId);
  const { data: allTags = [] } = useGetTagsQuery({});
  const [bulkAddTag] = useBulkAddTagMutation();
  const [bulkRemoveTag] = useBulkRemoveTagMutation();
  const [updateContact] = useUpdateContactMutation();
  const { data: contactDeals } = useGetDealsQuery({ contactId, size: 5 });
  const { data: contactTasks } = useGetTasksQuery({ contactId, size: 5 });
  const { data: duplicates = [] } = useGetContactDuplicatesQuery(contactId, { skip: !showManageActions });
  const [restoreContact, { isLoading: isRestoring }] = useRestoreContactMutation();
  const [deleteContactPermanently, { isLoading: isPurging }] = useDeleteContactPermanentlyMutation();
  const [showTaskDialog, setShowTaskDialog] = React.useState(false);
  const [showDealDialog, setShowDealDialog] = React.useState(false);
  const [showMergeDialog, setShowMergeDialog] = React.useState(false);
  const [confirmPurge, setConfirmPurge] = React.useState(false);
  const navigate = useNavigate();
  const { setPendingDialNumber, openCallDialog } = useCallDialog();
  const { setPendingRecipient, openMessageDialog } = useMessageDialog();

  const handleRestore = async () => {
    try {
      await restoreContact(contactId).unwrap();
      toast.success("Contact restored");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to restore contact"));
    }
  };

  const handlePurge = async () => {
    try {
      await deleteContactPermanently(contactId).unwrap();
      toast.success("Contact deleted permanently");
      setConfirmPurge(false);
      navigate("/contacts", { replace: true });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete contact"));
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">Contact not found</div>
    );
  }

  const phones = contact.phones ?? [];
  const primaryPhone = phones[0]?.phoneNumberE164 || phones[0]?.phoneNumber;
  const hasTraceableProperty = (contact.properties ?? []).some(
    (property) => !!property.address?.trim() && !!property.city?.trim() && !!property.state?.trim(),
  );
  const hasMultipleNumbers = phones.length > 1;

  const dial = (number?: string) => {
    if (number) {
      setPendingDialNumber(number);
      openCallDialog();
    }
  };

  return (
    <div className="flex flex-col h-full min-w-0">
      <div className="p-4 border-b border-border overflow-hidden">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12 shrink-0">
            {contact.photo && <AvatarImage src={contact.photo} />}
            <AvatarFallback className="bg-muted">
              {(() => {
                const initials = getInitials(contact.firstName, contact.lastName);
                if (initials !== "?") return initials;
                return <User className="h-5 w-5" />;
              })()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold truncate">
                {[contact.firstName, contact.lastName].filter(Boolean).join(" ") || primaryPhone || "Contact"}
              </span>
              {contact.type && (
                <Badge variant="outline" className="text-xs shrink-0">
                  {contact.type}
                </Badge>
              )}
              {contact.deletedAt && <span className="status-pill-neutral">Archived</span>}
              {contact.erasedAt && <span className="status-pill-neutral">Details erased</span>}
            </div>
            {(contact.company || contact.position) && (
              <p className="text-sm text-muted-foreground truncate">
                {[contact.position, contact.company].filter(Boolean).join(" at ")}
              </p>
            )}
          </div>
        </div>

        {(() => {
          const assignedTagIds = contact.contactTags?.map((contactTag) => contactTag.tag?.id).filter(Boolean) ?? [];
          const unassignedTags = allTags.filter((tag) => !assignedTagIds.includes(tag.id));

          const handleAddTag = async (tagId: string) => {
            try {
              await bulkAddTag({ tagId, contactIds: [contactId] }).unwrap();
            } catch {
              toast.error("Failed to add tag");
            }
          };

          const handleRemoveTag = async (tagId: string) => {
            try {
              await bulkRemoveTag({ tagId, contactIds: [contactId] }).unwrap();
            } catch {
              toast.error("Failed to remove tag");
            }
          };

          return (
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              {contact.contactTags?.map((contactTag) => (
                <Badge
                  key={contactTag.id}
                  className="text-xs text-white gap-1 pr-1"
                  style={{ backgroundColor: contactTag.tag?.color ?? "#6B7280" }}
                >
                  {contactTag.tag?.name}
                  <button
                    className="ml-0.5 rounded hover:bg-white/20 p-0.5"
                    onClick={() => contactTag.tag?.id && handleRemoveTag(contactTag.tag.id)}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </Badge>
              ))}
              {unassignedTags.length > 0 && (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button className="h-5 w-5 rounded-full border border-dashed border-muted-foreground/40 flex items-center justify-center hover:border-muted-foreground transition-colors">
                      <Plus className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48 max-h-60 overflow-auto">
                    {unassignedTags.map((tag) => (
                      <DropdownMenuItem
                        key={tag.id}
                        onSelect={(e) => {
                          e.preventDefault();
                          handleAddTag(tag.id);
                        }}
                        className="flex items-center gap-2"
                      >
                        <span
                          className="h-3 w-3 rounded-full shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })()}

        {contact.phones && contact.phones.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {contact.phones.map((phone, i) => (
              <Badge key={i} variant="secondary" className="text-xs font-normal">
                {phone.phoneType && <span className="font-medium mr-1">{phone.phoneType}</span>}
                {phone.phoneNumber || phone.phoneNumberE164}
                {phone.doNotCall && <PhoneOff className="h-3 w-3 ml-1 text-destructive" />}
                {phone.doNotText && <MessageSquareOff className="h-3 w-3 ml-1 text-destructive" />}
                {phone.status === ContactPhoneStatus.WRONG_NUMBER && (
                  <span className="ml-1 text-destructive">Wrong number</span>
                )}
                {phone.status === ContactPhoneStatus.DISCONNECTED && (
                  <span className="ml-1 text-destructive">Disconnected</span>
                )}
              </Badge>
            ))}
          </div>
        )}

        {contact.emails && contact.emails.some((contactEmail) => contactEmail.email) && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {contact.emails
              .filter((contactEmail) => contactEmail.email)
              .map((contactEmail) => (
                <Badge key={contactEmail.id} variant="secondary" className="text-xs font-normal">
                  {contactEmail.emailType && <span className="font-medium mr-1">{contactEmail.emailType}</span>}
                  <a
                    href={`mailto:${contactEmail.email}`}
                    className={contactEmail.unsubscribedAt ? "line-through hover:underline" : "hover:underline"}
                  >
                    {contactEmail.email}
                  </a>
                  {contactEmail.unsubscribedAt && (
                    <span className="ml-1 text-destructive" title="This address unsubscribed from marketing email">
                      Unsubscribed
                    </span>
                  )}
                </Badge>
              ))}
          </div>
        )}

        {contact.link && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            <Badge variant="secondary" className="text-xs font-normal">
              <Globe className="h-3 w-3 mr-1" />
              <a
                href={/^https?:\/\//i.test(contact.link) ? contact.link : `https://${contact.link}`}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                {contact.link}
              </a>
            </Badge>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-3">
          {hasMultipleNumbers ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Phone className="h-3.5 w-3.5 mr-1" />
                  Call
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {phones.map((phone, index) => (
                  <DropdownMenuItem
                    key={index}
                    onClick={() => dial(phone.phoneNumberE164 || phone.phoneNumber)}
                  >
                    <Phone className="h-3.5 w-3.5 mr-2" />
                    {phone.phoneType && <span className="font-medium mr-1">{phone.phoneType}</span>}
                    {phone.phoneNumber || phone.phoneNumberE164}
                    {phone.dncListStatus === DncListStatus.LITIGATOR ? (
                      <span className="ml-2 text-xs text-destructive">Litigator</span>
                    ) : (phone.doNotCall ||
                      phone.dncListStatus === DncListStatus.NATIONAL_LISTED ||
                      phone.dncListStatus === DncListStatus.STATE_LISTED) && (
                      <span className="ml-2 text-xs text-destructive">DNC</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" size="sm" onClick={() => dial(primaryPhone)} disabled={!primaryPhone}>
              <Phone className="h-3.5 w-3.5 mr-1" />
              Call
            </Button>
          )}
          <Button variant="outline" size="sm" disabled={!primaryPhone} onClick={() => {
            if (primaryPhone) {
              setPendingRecipient(primaryPhone);
              openMessageDialog();
            }
          }}>
            <MessageSquare className="h-3.5 w-3.5 mr-1" />
            Message
          </Button>
          <ContactDncButton phones={phones} />
          {contact.id && (
            <SkipTraceButton
              scope={SkipTraceScope.CONTACT}
              targetId={contact.id}
              targetName={getContactDisplayName(contact) ?? undefined}
              disabledReason={hasTraceableProperty ? undefined : "Add a property address to skip trace"}
            />
          )}
          {showViewButton && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/contacts/${contact.id}/detail`)}>
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              View
            </Button>
          )}
          {contact.id && <VConExportButton resource={{ type: "contact", id: contact.id }} />}
          {showManageActions && !contact.deletedAt && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/contacts/${contact.id}/edit`)}>
              <Pencil className="h-3.5 w-3.5 mr-1" />
              Edit
            </Button>
          )}
          {contact.deletedAt && (
            <Button variant="outline" size="sm" onClick={() => void handleRestore()} disabled={isRestoring}>
              <ArchiveRestore className="h-3.5 w-3.5 mr-1" />
              Restore
            </Button>
          )}
          {showManageActions && !contact.deletedAt && (
            <Button variant="outline" size="sm" onClick={() => setShowMergeDialog(true)}>
              <Merge className="h-3.5 w-3.5 mr-1" />
              Merge
            </Button>
          )}
          {showManageActions && contact.deletedAt && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" title="Options">
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="text-destructive" onClick={() => setConfirmPurge(true)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete permanently
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {showManageActions && !contact.deletedAt && duplicates.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <span>
              {duplicates.length === 1
                ? "1 other contact shares a phone number or email with this one."
                : `${duplicates.length} other contacts share a phone number or email with this one.`}
            </span>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowMergeDialog(true)}>
              Review duplicates
            </Button>
          </div>
        )}
        {headerExtra}
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Handshake className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Deals</span>
            </div>
            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setShowDealDialog(true)} title="New deal">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          {contactDeals && contactDeals.items.length > 0 ? (
            <div className="space-y-1.5">
              {contactDeals.items.map((deal) => (
                <button
                  key={deal.id}
                  className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-muted transition-colors text-left"
                  onClick={() => navigate(`/deals/${deal.id}`)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {deal.propertyAddress || "Untitled deal"}
                    </div>
                    {deal.offerPrice != null && (
                      <div className="text-xs text-muted-foreground">
                        ${Number(deal.offerPrice).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <Badge
                    className="text-xs text-white shrink-0"
                    style={{ backgroundColor: deal.stage?.color ?? "#6B7280" }}
                  >
                    {deal.stage?.name}
                  </Badge>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No deals</p>
          )}
        </div>
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Tasks</span>
            </div>
            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setShowTaskDialog(true)}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          {contactTasks && contactTasks.items.length > 0 ? (
            <div className="space-y-1.5">
              {contactTasks.items.map((task) => {
                const overdue = task.dueDate && task.status !== "DONE" && new Date(task.dueDate) < new Date(new Date().toDateString());
                return (
                  <div
                    key={task.id}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left ${task.status === "DONE" ? "opacity-50" : ""}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm ${task.status === "DONE" ? "line-through text-muted-foreground" : ""}`}>
                        {task.title}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{STATUS_LABELS[task.status]}</span>
                        {task.dueDate && (
                          <span className={`flex items-center gap-0.5 ${overdue ? "text-red-500 font-medium" : ""}`}>
                            <Calendar className="h-3 w-3" />
                            {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No tasks</p>
          )}
        </div>
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2 mb-2">
            <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Custom fields</span>
          </div>
          <CustomFieldsEditor
            fields={contact.customFields}
            onSave={(customFields) => updateContact({ id: contact.id, data: { customFields } }).unwrap()}
          />
        </div>
        <ContactConsentStatus contact={contact} />
        <ActivityTimeline contactId={contactId} compact={compact} />
      </div>

      <TaskFormDialog
        open={showTaskDialog}
        onOpenChange={setShowTaskDialog}
        defaultContactId={contactId}
      />
      <DealFormDialog open={showDealDialog} onOpenChange={setShowDealDialog} defaultContact={contact} />
      {showManageActions && (
        <MergeContactDialog contact={contact} open={showMergeDialog} onOpenChange={setShowMergeDialog} />
      )}
      <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact permanently</AlertDialogTitle>
            <AlertDialogDescription>
              A contact with no calls, texts, deals or tasks is removed entirely. One with history keeps its calls and texts under the phone number only; the name and every other detail are erased and cannot be recovered. Consent and do-not-call records on the phone stay.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handlePurge()} disabled={isPurging} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContactActivity;
